import React, { useState, useEffect } from 'react';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { runEvalRAG, fetchEvalHistory } from '../../../shared/services/api';
import type { EvalReport, EvalCaseResult } from '../../../shared/services/api';
import { DASH_CONTENT_CARD, DASH_CARD_TITLE } from '../../../shared/components/dashboard';

// ═══════════════════════════════════════════════════════════════
// AdminIAEvalsTab — Evaluaciones RAG
// ═══════════════════════════════════════════════════════════════

/* ── Styles ── */

const METRIC_BAR_BG: React.CSSProperties = {
  height: 20,
  borderRadius: 4,
  backgroundColor: '#f3f4f6',
  flex: 1,
  overflow: 'hidden',
};

const TH: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#6b7280',
  borderBottom: '2px solid #e5e7eb',
  textAlign: 'left',
};

const TD: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 12,
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'top',
};

const BTN_PRIMARY: React.CSSProperties = {
  padding: '8px 20px',
  background: '#1F3864',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/* ── Score color helper ── */

function scoreColor(score: number): string {
  if (score >= 0.8) return '#16a34a';
  if (score >= 0.5) return '#d97706';
  return '#dc2626';
}

function scoreBg(score: number): string {
  if (score >= 0.8) return '#dcfce7';
  if (score >= 0.5) return '#fef3c7';
  return '#fee2e2';
}

/* ── Metric bar component ── */

function MetricBar({ label, value }: { label: string; value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 500, width: 120, color: '#374151' }}>{label}</span>
      <div style={METRIC_BAR_BG}>
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            backgroundColor: scoreColor(value),
            borderRadius: 4,
            transition: 'width 0.5s ease',
          }}
        />
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          width: 42,
          textAlign: 'right',
          color: scoreColor(value),
        }}
      >
        {pct}%
      </span>
    </div>
  );
}

/* ── Score badge ── */

function ScoreBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: scoreBg(value),
        color: scoreColor(value),
      }}
    >
      {pct}%
    </span>
  );
}

/* ── Main component ── */

export function AdminIAEvalsTab() {
  const { t } = useI18n();

  const [running, setRunning] = useState(false);
  const [currentReport, setCurrentReport] = useState<EvalReport | null>(null);
  const [history, setHistory] = useState<EvalReport[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedCase, setExpandedCase] = useState<string | null>(null);
  const [limitInput, setLimitInput] = useState('');

  // Load history on mount
  useEffect(() => {
    fetchEvalHistory()
      .then((res) => setHistory(res.data))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, []);

  const handleRunEval = async () => {
    setRunning(true);
    setCurrentReport(null);
    try {
      const limit = limitInput ? parseInt(limitInput, 10) : undefined;
      const res = await runEvalRAG(limit && !isNaN(limit) ? limit : undefined);
      setCurrentReport(res.data);
      setHistory((prev) => [res.data, ...prev]);
    } catch (err) {
      console.error('Error running eval:', err);
    } finally {
      setRunning(false);
    }
  };

  const toggleCase = (id: string) => {
    setExpandedCase((prev) => (prev === id ? null : id));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ── Run evaluation card ── */}
      <div style={DASH_CONTENT_CARD}>
        <div style={{ ...DASH_CARD_TITLE, marginBottom: 12 }}>Ejecutar evaluacion RAG</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>Limite de casos (vacio = todos):</label>
          <input
            type="number"
            min={1}
            max={20}
            value={limitInput}
            onChange={(e) => setLimitInput(e.target.value)}
            style={{
              width: 60,
              padding: '4px 8px',
              border: '1px solid #d5d5d5',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
            disabled={running}
          />
          <button
            onClick={handleRunEval}
            disabled={running}
            style={{
              ...BTN_PRIMARY,
              opacity: running ? 0.7 : 1,
              cursor: running ? 'default' : 'pointer',
            }}
          >
            {running ? 'Ejecutando...' : 'Ejecutar evaluacion'}
          </button>
        </div>
        {running && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            <LoadingSpinner text="Evaluando dataset RAG (esto puede tardar varios minutos)..." />
          </div>
        )}
      </div>

      {/* ── Current report summary ── */}
      {currentReport && (
        <div style={DASH_CONTENT_CARD}>
          <div style={{ ...DASH_CARD_TITLE, marginBottom: 12 }}>Resultado de la evaluacion</div>

          {/* KPI row */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <KpiMini
              label="Casos"
              value={`${currentReport.completedCases}/${currentReport.totalCases}`}
            />
            <KpiMini
              label="Fallidos"
              value={String(currentReport.failedCases)}
              alert={currentReport.failedCases > 0}
            />
            <KpiMini
              label="Duracion"
              value={`${Math.round(currentReport.totalDurationMs / 1000)}s`}
            />
          </div>

          {/* Metric bars */}
          <MetricBar label="Faithfulness" value={currentReport.avgFaithfulness} />
          <MetricBar label="Relevancy" value={currentReport.avgRelevancy} />
          <MetricBar label="Context Precision" value={currentReport.avgContextPrecision} />
          <MetricBar label="Context Recall" value={currentReport.avgContextRecall} />

          {/* Results table */}
          <div style={{ marginTop: 16, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>ID</th>
                  <th style={TH}>Pregunta</th>
                  <th style={TH}>Cat.</th>
                  <th style={TH}>Faith.</th>
                  <th style={TH}>Relev.</th>
                  <th style={TH}>Prec.</th>
                  <th style={TH}>Recall</th>
                  <th style={TH}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {currentReport.results.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr
                      onClick={() => toggleCase(r.id)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: expandedCase === r.id ? '#f9fafb' : undefined,
                      }}
                    >
                      <td style={{ ...TD, fontWeight: 600, fontFamily: 'monospace' }}>{r.id}</td>
                      <td style={{ ...TD, maxWidth: 280 }}>
                        <div
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.query}
                        </div>
                      </td>
                      <td style={TD}>
                        <span
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            borderRadius: 4,
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                          }}
                        >
                          {r.category}
                        </span>
                      </td>
                      <td style={TD}>
                        <ScoreBadge value={r.scores.faithfulness} />
                      </td>
                      <td style={TD}>
                        <ScoreBadge value={r.scores.relevancy} />
                      </td>
                      <td style={TD}>
                        <ScoreBadge value={r.scores.contextPrecision} />
                      </td>
                      <td style={TD}>
                        <ScoreBadge value={r.scores.contextRecall} />
                      </td>
                      <td style={TD}>
                        {r.error ? (
                          <span style={{ color: '#dc2626', fontSize: 11 }}>Error</span>
                        ) : (
                          <span style={{ color: '#16a34a', fontSize: 11 }}>OK</span>
                        )}
                      </td>
                    </tr>
                    {expandedCase === r.id && (
                      <tr>
                        <td
                          colSpan={8}
                          style={{
                            padding: 16,
                            backgroundColor: '#f9fafb',
                            borderBottom: '1px solid #e5e7eb',
                          }}
                        >
                          <CaseDetail result={r} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── History ── */}
      <div style={DASH_CONTENT_CARD}>
        <div style={{ ...DASH_CARD_TITLE, marginBottom: 12 }}>Historial de evaluaciones</div>
        {historyLoading ? (
          <LoadingSpinner text={t.admin.via.cargando} />
        ) : history.length === 0 ? (
          <p style={{ fontSize: 13, color: '#6b7280' }}>No hay evaluaciones anteriores.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Fecha</th>
                <th style={TH}>Casos</th>
                <th style={TH}>Faith.</th>
                <th style={TH}>Relev.</th>
                <th style={TH}>Prec.</th>
                <th style={TH}>Recall</th>
                <th style={TH}>Duracion</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} onClick={() => setCurrentReport(h)} style={{ cursor: 'pointer' }}>
                  <td style={TD}>{new Date(h.timestamp).toLocaleString()}</td>
                  <td style={TD}>
                    {h.completedCases}/{h.totalCases}
                  </td>
                  <td style={TD}>
                    <ScoreBadge value={h.avgFaithfulness} />
                  </td>
                  <td style={TD}>
                    <ScoreBadge value={h.avgRelevancy} />
                  </td>
                  <td style={TD}>
                    <ScoreBadge value={h.avgContextPrecision} />
                  </td>
                  <td style={TD}>
                    <ScoreBadge value={h.avgContextRecall} />
                  </td>
                  <td style={TD}>{Math.round(h.totalDurationMs / 1000)}s</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div
      style={{
        padding: '8px 16px',
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        backgroundColor: alert ? '#fef2f2' : '#f9fafb',
        minWidth: 80,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 500, color: '#6b7280', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: alert ? '#dc2626' : '#111827' }}>
        {value}
      </div>
    </div>
  );
}

function CaseDetail({ result }: { result: EvalCaseResult }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 12 }}>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#374151' }}>
          Respuesta esperada:
        </div>
        <div style={{ color: '#6b7280', lineHeight: 1.5 }}>{result.expectedAnswer}</div>
      </div>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#374151' }}>
          Respuesta generada:
        </div>
        <div style={{ color: '#6b7280', lineHeight: 1.5 }}>
          {result.generatedAnswer || '(sin respuesta)'}
        </div>
      </div>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#374151' }}>Docs esperados:</div>
        <div style={{ fontFamily: 'monospace', color: '#6b7280' }}>
          {result.expectedDocCodes.join(', ')}
        </div>
      </div>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#374151' }}>Docs recuperados:</div>
        <div style={{ fontFamily: 'monospace', color: '#6b7280' }}>
          {result.retrievedDocCodes.join(', ') || '(ninguno)'}
        </div>
      </div>
      {result.error && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#dc2626' }}>Error:</div>
          <div style={{ color: '#dc2626', fontSize: 11 }}>{result.error}</div>
        </div>
      )}
      <div style={{ gridColumn: '1 / -1', color: '#9ca3af', fontSize: 11 }}>
        Duracion: {result.durationMs}ms
      </div>
    </div>
  );
}
